import { motion } from "framer-motion";

interface PinterestBoardProps {
  title: string;
  photoIds: string[];
  stats: { pins: string; followers: string; };
  className?: string;
  additionalData: {
    title: string;
    price: string;
    link: string;
  }[]
}

const styles = `
  @keyframes dash {
    to {
      stroke-dashoffset: 20;
    }
  }

  .animate-dash {
    animation: dash 1.5s linear infinite;
  }
`;

export function ShopifyPreview() {
  return (
    <motion.div 
      className="absolute left-0 sm:left-4 w-52 sm:w-64 md:w-72 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden opacity-90 transition-all duration-300"
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 0.9, x: 0 }}
      transition={{ duration: 0.6 }}
      whileHover={{ scale: 1.02, opacity: 1 }}
    >
      <div className="p-2 bg-gray-800 flex items-center">
        <img src="/icons8-shopify.svg" alt="Shopify" className="h-6" />
        <span className="ml-2 text-white font-semibold">Your Shopify Product</span>
      </div>
      <div className="p-4 space-y-3">
        <motion.div 
          className="aspect-square rounded-lg bg-gray-100 mb-2 overflow-hidden"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.2 }}
        >
          <img
            src="https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=400"
            alt="Product"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <h3 className="font-medium text-gray-900">Vintage Denim Jacket</h3>
        <div className="flex justify-between items-center">
          <span className="text-lg font-semibold text-gray-900">$89.99</span>
          <span className="text-sm text-green-600">In Stock</span>
        </div>
        <p className="text-sm text-gray-600">Classic vintage-style denim jacket with distressed details and premium comfort.</p>
      </div>
    </motion.div>
  );
}

export function ArrowAnimation() {
  return (
    <div className="hidden bg-transparent sm:block absolute left-[45%] translate-x-[-50%] top-[70%] sm:top-[85%] h-full z-10">
      <img src="/icons8-pfeil100.png" alt="Arrow" className="rotate-[140deg] w-[40px] sm:w-[60px] md:w-[80px] -scale-x-100" />
    </div>
  );
}

export function PinterestPreviews() {
  return (
    <>
      <PinterestBoard
        title="Summer Collection"
        photoIds={[
          "photo-1496747611176-843222e1e57c",
          "photo-1469334031218-e382a71b716b",
          "photo-1483985988355-763728e1935b",
          "photo-1515886657613-9f3515b0c78f"
        ]}
        additionalData={[]}
        stats={{ pins: "24", followers: "2.4k" }}
        className="-rotate-6 absolute right-[15%] sm:right-[10%] hidden sm:block"
      />
      <PinterestBoard
        title="New Arrivals"
        photoIds={[
          "photo-1485968579580-b6d095142e6e",
          "photo-1434389677669-e08b4cac3105",
          "photo-1475180098004-ca77a66827be",
          "photo-1515886657613-9f3515b0c78f"
        ]}
        additionalData={[
          {
            title: "Vintage Denim Jacket",
            price: "$89.99",
            link: "Shop Now"
          },
          {
            title: "Pashmina Scarf",
            price: "$19.99",
            link: "Shop Now"
          }
        ]}
        stats={{ pins: "16", followers: "3.1k" }}
        className="absolute right-[5%] sm:right-[0%] rotate-6"
      />
    </>
  );
}

function PinterestBoard({ title, photoIds, stats, additionalData, className = "" }: PinterestBoardProps) {
  return (
    <>
      <div className={`bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden w-52 sm:w-64 md:w-80 hover:shadow-2xl transition-all duration-300 ${className}`}>
        <div className="p-2 sm:p-3 bg-[#E60023] flex items-center justify-between">
          <div className="flex items-center">
            <img src="/icons8-pinterest.svg" alt="Pinterest" className="h-5 sm:h-6" />
            <span className="ml-2 text-white font-semibold text-sm sm:text-base">{title}</span>
          </div>
          <div className="flex items-center space-x-2 text-white">
            <span className="text-xs sm:text-sm">{stats.followers} followers</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1 sm:gap-2 p-2 sm:p-3">
          {photoIds.map((photoId, index) => (
            <div key={photoId} className="aspect-[3/4] rounded-lg overflow-hidden shadow-sm relative group">
              <img
                src={`https://images.unsplash.com/${photoId}?w=300&q=80`}
                alt="Fashion Pin"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              {additionalData[index] && (
                <div className="absolute bottom-0 left-0 right-0 p-1.5 sm:p-2 bg-gradient-to-t from-black/60 to-transparent">
                  <p className="text-white text-xs sm:text-sm font-medium">{additionalData[index].title}</p>
                  <p className="text-gray-200 text-[10px] sm:text-xs">{additionalData[index].price} - {additionalData[index].link}</p>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="px-2 sm:px-3 pb-2 sm:pb-3">
          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600">
            <span>{stats.pins} pins</span>
            <span>{stats.followers} followers</span>
          </div>
        </div>
      </div>
    </>
  );
} 